import type { FoodDietClassName } from '@steado/admin-contract/foods';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import { DietAnswer } from './DietAnswer';
import { JainAnswer } from './JainAnswer';

export interface BulkOutcome {
  readonly succeeded: number;
  readonly failed: { readonly id: string; readonly message: string }[];
}

interface BulkVerifyDialogProps {
  readonly ids: readonly string[];
  /**
   * The selected photo-scan estimates and the diet class each is listed with.
   * Verifying one states its class (see `DietAnswer`).
   */
  readonly estimates: ReadonlyMap<string, FoodDietClassName>;
  /**
   * `jainExcluded` is the operator's one answer for the whole selection;
   * `dietClass` is the confirmed class, sent for an estimate only.
   */
  readonly onRun: (
    id: string,
    jainExcluded: boolean,
    dietClass: FoodDietClassName | undefined,
  ) => Promise<unknown>;
  readonly onDone: (outcome: BulkOutcome) => void;
  readonly onCancel: () => void;
}

/**
 * Verifies a selection one row at a time.
 *
 * There is no batch endpoint, and this does not pretend there is: the calls go
 * one after another through the same per-food route the drawer uses, so every
 * one writes its own audit row. That also means the operation is not atomic,
 * and the honest thing is to report which rows failed rather than roll back
 * work that already succeeded — a half-verified selection is a real state, and
 * hiding it would leave the operator unable to tell which half.
 *
 * Sequential rather than `Promise.all`: twenty concurrent writes to the same
 * table from one click is a spike the API does not need, and the progress
 * count is only meaningful in order.
 */
export function BulkVerifyDialog({ ids, estimates, onRun, onDone, onCancel }: BulkVerifyDialogProps) {
  const [done, setDone] = useState(0);
  const [isRunning, setRunning] = useState(false);
  const [jainExcluded, setJainExcluded] = useState<boolean | null>(null);
  // One estimate gets the class asked outright. Several are confirmed as
  // listed, since each carries its own: the operator ticks that they checked
  // the diet column, and each row is verified with the class it shows.
  const [dietAnswer, setDietAnswer] = useState<FoodDietClassName | null>(null);
  const [listedClassesChecked, setListedClassesChecked] = useState(false);
  const listedClassesId = useId();
  const [onlyId] = ids;
  const singleEstimate = ids.length === 1 && onlyId !== undefined ? estimates.get(onlyId) : undefined;
  const dietAnswered =
    singleEstimate !== undefined ? dietAnswer !== null : estimates.size === 0 || listedClassesChecked;

  const run = async (answer: boolean): Promise<void> => {
    setRunning(true);
    const failed: { id: string; message: string }[] = [];
    let succeeded = 0;

    for (const id of ids) {
      try {
        await onRun(id, answer, singleEstimate !== undefined ? (dietAnswer ?? undefined) : estimates.get(id));
        succeeded += 1;
      } catch (error) {
        failed.push({ id, message: error instanceof Error ? error.message : 'Unknown failure.' });
      }
      setDone((count) => count + 1);
    }

    setRunning(false);
    onDone({ succeeded, failed });
  };

  return (
    <ConfirmDialog
      title={ids.length === 1 ? 'Verify this food?' : `Verify ${String(ids.length)} foods?`}
      body={
        isRunning ? (
          `Verifying… ${String(done)} of ${String(ids.length)} done.`
        ) : (
          <div className="flex flex-col gap-3">
            <p>
              Verifying promotes their numbers from “estimated” to plain in the app, and makes an estimate
              plannable. Each is verified separately, so a failure part-way leaves the ones before it
              verified.
            </p>
            <JainAnswer value={jainExcluded} onChange={setJainExcluded} disabled={false} count={ids.length} />
            {singleEstimate !== undefined ? (
              <DietAnswer
                value={dietAnswer}
                provisional={singleEstimate}
                onChange={setDietAnswer}
                disabled={false}
              />
            ) : estimates.size > 0 ? (
              <span className="flex items-start gap-2">
                <Checkbox
                  id={listedClassesId}
                  checked={listedClassesChecked}
                  onCheckedChange={(checked) => {
                    setListedClassesChecked(checked === true);
                  }}
                />
                <Label htmlFor={listedClassesId} className="text-[13px] font-normal leading-snug">
                  {estimates.size === 1
                    ? 'One is a photo-scan estimate'
                    : `${String(estimates.size)} are photo-scan estimates`}
                  , whose diet class was worked out from the model’s ingredients. I checked the diet column,
                  and each is right as listed.
                </Label>
              </span>
            ) : null}
          </div>
        )
      }
      confirmLabel={ids.length === 1 ? 'Verify' : `Verify ${String(ids.length)}`}
      confirmDisabled={jainExcluded === null || !dietAnswered}
      onConfirm={() => {
        if (jainExcluded !== null) void run(jainExcluded);
      }}
      onCancel={onCancel}
      isPending={isRunning}
    />
  );
}
